import { useMemo, useEffect } from "react";
import { useIntl } from "react-intl";
import { useToast } from "@chakra-ui/react";

import { useSettings } from "utils/settingsContext";
import { useLanguageContext } from "utils/LanguageProvider";
import {
  getLocalizationSettings,
  maintainedLanguages,
  getDefaultLocale,
} from "utils/LanguageProvider/utils";

import { modelLanguageOptionsFromSettings } from "../utils";

/**
 * @function useLanguageSelector Hook to manage settings/context data for Language Selector
 * @requires Providers - Session, Settings and Language Provider
 * @returns
 */
export const useLanguageSelector = () => {
  const toast = useToast();
  const { setLanguage, locale, error, selectedLocale, onInit, isInitCompleted } =
    useLanguageContext();
  const { formatMessage } = useIntl();
  const settings = useSettings();
  const { enabled, languages: publisherLangauges } = useMemo(() => {
    return getLocalizationSettings(settings, {
      enabled: false,
      languages: [],
    });
  }, [settings]);

  const languages = modelLanguageOptionsFromSettings(publisherLangauges, maintainedLanguages);

  // Set client default based on Publisher settings
  useEffect(() => {
    const { locale: clientLocale } = getDefaultLocale(maintainedLanguages);
    if (enabled && languages.some(({ value }) => value === clientLocale) && !isInitCompleted) {
      setLanguage(clientLocale);
      onInit();
    }
  }, [enabled, isInitCompleted, languages, onInit, setLanguage]);

  // Toast Error messaging
  useEffect(() => {
    if (error && selectedLocale !== locale) {
      const label = maintainedLanguages[selectedLocale]?.name || selectedLocale;
      toast({
        title: formatMessage({
          defaultMessage: "Error selecting language",
          description: "Error alert - title",
        }),
        description: formatMessage(
          {
            defaultMessage: "Could not load {languageFile}",
            description: "Error alert - message",
          },
          {
            languageFile: label,
          }
        ),
        status: "error",
        isClosable: true,
      });
    }
  }, [error, toast, selectedLocale, locale, formatMessage]);

  return {
    show: enabled,
    languages,
    setLanguage,
    locale,
  };
};
